import { Fragment, useEffect } from 'react';
import { Type } from '@spotify-internal/encore-web';

import withTranslation from 'lib/shared/i18n/withTranslation';
import { withConfig } from 'context/Config/ConfigProvider';
import TopicList from 'components/TopicList/TopicList';
import { ConfigProps, TranslationProps } from 'etc/types';
import { StepProps } from 'containers/ContentReporting/ContentPolicyReporting/utils/types';
import { reasonToJSON } from 'proto/generated/tintin_content_policy';
import {
  Field,
  ReasonOptions,
} from 'components/ContentReporting/ContentPolicyReporting/content-policy-reporting-config';
import analytics, {
  StepLabel,
} from 'components/ContentReporting/ContentPolicyReporting/policy-reporting-analytics-utils';
import { convertAbsoluteUrlToPlain, useIsPlainPage } from 'context/PlainPage/plainPage';
import { SUPPORT_EMAIL } from 'lib/shared/config/config';

export interface Step1DescriptionProps extends ConfigProps, StepProps, TranslationProps {
  isPodcastReportingEnabled?: boolean;
  isMusicVideosReportingEnabled?: boolean;
}

const Step1Description = ({
  t,
  traw,
  config,
  location,
  onStepSubmit,
  onFieldChange,
  isPodcastReportingEnabled,
  isMusicVideosReportingEnabled,
}: Step1DescriptionProps): JSX.Element => {
  useEffect(
    () => analytics.trackVirtualPageView(location.pathname, location.search, StepLabel.Step1Reason),
    [location.pathname, location.search],
  );

  const onOptionClick = (option: string) => {
    // this was a tradeoff whether we need to know the exact field name here
    // vs pass it as a prop, and thus break the consistency with other step components
    onFieldChange({
      fieldIdentifier: Field.REASON,
      value: option,
      valid: true,
    });
    onStepSubmit();
  };

  const isPlainPage = useIsPlainPage();

  const contactUrl = isPlainPage
    ? `mailto:${SUPPORT_EMAIL}`
    : `${config.supportSiteUrl('/contact-spotify-support/')}${location.search}`;

  const legalFormBaseUrl = `${config.supportSiteUrl('/report-content/')}${location.search}`;
  const legalFormUrl = isPlainPage ? convertAbsoluteUrlToPlain(legalFormBaseUrl) : legalFormBaseUrl;

  const getContentReportingIntroPageTranslationKey = () => {
    if (isMusicVideosReportingEnabled && isPodcastReportingEnabled) {
      return 'content_reporting_intro_page_explainer_part1_podcast_reporting_enabled_music_video';
    }

    if (isMusicVideosReportingEnabled) {
      return 'content_reporting_intro_page_explainer_part1_podcast_music_video';
    }

    if (isPodcastReportingEnabled) {
      return 'content_reporting_intro_page_explainer_part1_podcast_reporting_enabled';
    }

    return 'content_reporting_intro_page_explainer_part1_podcast_reporting';
  };

  const contentReportingIntroPageTranslationKey = getContentReportingIntroPageTranslationKey();

  return (
    <Fragment>
      <Type.h1 paddingBottom="0.67em" variant="altoBrio">
        {t('content_reporting_intro_page_title')}
      </Type.h1>
      <Type.p paddingBottom="1em" variant="ballad">
        {traw(contentReportingIntroPageTranslationKey, {
          url: contactUrl,
        })}
      </Type.p>

      <Type.p paddingBottom="1em" variant="ballad">
        {traw('content_reporting_intro_page_explainer_part2', {
          url: legalFormUrl,
        })}
      </Type.p>
      <Type.p paddingBottom="1em" variant="ballad">
        {traw('content_reporting_intro_page_explainer_part3')}
      </Type.p>
      <Type.h3 paddingBottom="1em" variant="celloCanon">
        {t('content_reporting_intro_page_sub_title')}
      </Type.h3>
      <TopicList
        topics={ReasonOptions.map(topic => ({
          title: t(topic.label),
          value: reasonToJSON(topic.value),
        }))}
        onClickFunction={onOptionClick}
      />
    </Fragment>
  );
};

export default withTranslation('content_policy_reporting')(withConfig(Step1Description));
